import update from 'immutability-helper'
import md5 from 'md5'
import PubSub from 'pubsub-js'
import { SLICE_SIZE, MAX_THREAD_COUNT, PubSubTopic, UPLOAD_STATUS } from 'constants.js'
import _throttle from 'lodash/throttle'
import fetch from './fetch'
import { cgiCallWrapper } from './cgiCallWrapper'

const store = {
    // 文件切片后的列表, [[file1 slices...], [], []]
    fileSliceList: [],
    // 文件进度数据{ 'filename md5': {...}}
    progressData: {},
    activeThreadsCount: 0,
}

const sliceUploadFetch = params => {
    return fetch({
        url: '/upload/slice',
        method: 'POST',
        opts: {
            headers: {
                'Content-Type': 'multipart/form-data;charset=UTF-8',
            },
        },
        data: params,
    })
}

const cancelSliceUploadFetch = params => {
    return fetch({
        url: '/upload/cancel',
        method: 'POST',
        data: params,
    })
}

export const __getInnerDataForTest = () => {
    return store
}

// 初始化分片池和文件上传状态state
export const fileSlice = taskInfo => {
    const addProgressData = {}
    const fileNames = []
    // 分片资源池
    const addFileSliceList = []
    const files = taskInfo.files
    Object.keys(files).forEach(key => {
        const file = files[key]
        const { name: originName, size } = file
        const name = originName.replace('.apk', '')
        const fileKey = md5(name)
        const channelPkgInfo = file.channelPkgInfo
        if (fileNames.includes(fileKey)) {
            throw new Error('file names duplicate')
        } else {
            fileNames.push(fileKey)
        }
        const fileSlicePool = []
        const slices = []
        // 计算文件分片信息, 小于16M的不分片，直接作为一片
        const totalSlice = Math.ceil(size / SLICE_SIZE)
        for (let i = 0; i < totalSlice; i++) {
            let sliceFile
            // 只有一片就不用分片了
            if (totalSlice === 1) {
                sliceFile = file
            } else {
                sliceFile = file.slice(i * SLICE_SIZE, (i + 1) * SLICE_SIZE)
            }
            fileSlicePool.push({
                fileKey,
                slice: i,
                totalSlice,
                total: size,
                file: sliceFile,
            })
            slices.push(0)
        }
        addFileSliceList.push(fileSlicePool)

        // 保存state，用于展示文件上传进度
        delete taskInfo.files
        addProgressData[fileKey] = {
            ...taskInfo,
            channelPkgInfo,
            name, // 文件名
            status: 0, // 文件上传状态: 0: 待上传,1: 上传中, 2: 上传完成, 3: 上传失败
            slices, // 各个分片的上传状态，数组
            totalSlice, // 总分片数
        }
    })

    return {
        addProgressData,
        addFileSliceList,
    }
}

export const updateProgress = (state, data) => {
    // 修改文件进度到
    const { status, fileKey, slice, uploadId, objectToken, sha1, completed, errorMessage } = data
    const progressPatch = {}
    if (uploadId) progressPatch.uploadId = uploadId
    if (objectToken) progressPatch.objectToken = objectToken
    if (sha1) progressPatch.sha1 = sha1
    if (completed) progressPatch.completed = true
    if (errorMessage) progressPatch.errorMessage = errorMessage

    // 更新状态 当前分片的状态
    let newState = update(state, {
        [fileKey]: {
            slices: {
                [slice]: {
                    $set: status,
                },
            },
            $merge: progressPatch,
        },
    })

    // 上传失败，整个文件上传失败
    if (status === 3) {
        newState = update(newState, {
            [fileKey]: {
                status: {
                    $set: status,
                },
            },
        })
        // 上传成功，检查是否所有分片都已上传成功
    } else if (status === 2) {
        const progressData = newState[fileKey]
        const { slices, completed } = progressData
        const isAllDone = slices.every(item => item === 2)
        if (isAllDone && completed) {
            // 将上传成功到文件删除
            newState = update(newState, {
                $unset: [fileKey],
            })
        }
        // 上传中
    } else if (status === 1) {
        if (newState[fileKey].status === 0) {
            newState = update(newState, {
                [fileKey]: {
                    status: {
                        $set: status,
                    },
                },
            })
        }
    }
    return newState
}
export const taskRunner = async (_getTaskData, _taskRunner, _releaseThread) => {
    let taskData
    while ((taskData = _getTaskData())) {
        await _taskRunner(taskData)
    }
    return _releaseThread()
}

const updateProgressData = slice => {
    store.progressData = updateProgress(store.progressData, slice)
    publishProgress(store.progressData)
    return store.progressData
}

const genSliceFetchFormData = _store => slice => {
    const currentFileProgress = _store.progressData[slice.fileKey]
    const { uploadId = '', appInfo = {}, channelPkgInfo = {} } = currentFileProgress
    const channelName = channelPkgInfo.channelName || currentFileProgress.name
    const paramsObject = {
        uploadId: uploadId || '', // 分片上传ID
        objectToken: currentFileProgress.objectToken || '', // 服务端生成的临时对象标识
        appId: appInfo.appId, //
        channelName: channelName,
        channelKey: channelPkgInfo.channelKey || '',
        file: new File([slice.file], channelName), // 分片文件内容, 不应低于1M, 建议大小20M; 总大小20M以下不建议走分片上传接
        slice: slice.slice + 1, // 第几片, 从1开始
        totalSlice: slice.totalSlice, // 总分片数
        total: slice.total, // 总大小
    }
    const formData = new FormData()
    Object.keys(paramsObject).forEach(key => {
        formData.append(key, paramsObject[key])
    })
    return formData
}

export const sliceUpload = (_updateProgressData, _genSliceFetchFormData) => {
    return async slice => {
        const newSlice = Object.assign({}, slice)
        try {
            newSlice.status = 1
            _updateProgressData(newSlice)
            const result = await cgiCallWrapper(sliceUploadFetch, _genSliceFetchFormData(newSlice), false)
            if (result.b) {
                newSlice.uploadId = result.b.uploadId
                if (result.b.objectToken) {
                    newSlice.objectToken = result.b.objectToken
                }
                newSlice.sha1 = result.b.sha1
                if (result.b.completed) {
                    newSlice.completed = true
                }
                newSlice.status = UPLOAD_STATUS.DONE
                _updateProgressData(newSlice)
            } else {
                throw new Error(result.d)
            }
        } catch (e) {
            newSlice.status = UPLOAD_STATUS.FAIL
            newSlice.errorMessage = e.message || '网络出现问题，请重新尝试'
            _updateProgressData(newSlice)
        }
    }
}

const publishProgress = _throttle(_progressData => {
    PubSub.publish(PubSubTopic, _progressData)
}, 50)

const getOneSliceFromList = _fileSliceList => () => {
    let nextSlice = _fileSliceList.shift()
    if (nextSlice) {
        const currentFileProgress = store.progressData[nextSlice.fileKey] || {}
        const { status } = currentFileProgress
        // 只有待上传或者上传中的文件才返回下一分片, 主要过滤取消的任务或者失败的任务
        if (status === UPLOAD_STATUS.WAIT || status === UPLOAD_STATUS.UPLOADING) {
            return nextSlice
        } else {
            return false
        }
    } else {
        return false
    }
}
const releaseThread = () => {
    store.activeThreadsCount = Math.max(store.activeThreadsCount - 1, 0)
    return store.activeThreadsCount
}
const startTaskRunnerThreads = () => {
    let intervalID = setInterval(() => {
        // 如果有空闲的线程，且待上传文件池不为空，则取文件资源消费
        while (store.activeThreadsCount < MAX_THREAD_COUNT && store.fileSliceList && store.fileSliceList.length > 0) {
            let fileSlices = store.fileSliceList.shift()
            const _getTaskData = getOneSliceFromList(fileSlices)
            const _genSliceFetchFormData = genSliceFetchFormData(store)
            const _taskRunner = sliceUpload(updateProgressData, _genSliceFetchFormData)
            taskRunner(_getTaskData, _taskRunner, releaseThread)
            store.activeThreadsCount = Math.min(store.activeThreadsCount + 1, MAX_THREAD_COUNT)
        }
        if (store.fileSliceList && store.fileSliceList.length === 0) {
            clearInterval(intervalID)
        }
    }, 1000)
}
export const submitTask = (taskInfo = {}) => {
    const { addProgressData, addFileSliceList } = fileSlice(taskInfo)
    store.fileSliceList = store.fileSliceList.concat(addFileSliceList)
    store.progressData = Object.assign({}, store.progressData, addProgressData)
    startTaskRunnerThreads()
    return addProgressData
}

export const cancelTask = fileKey => {
    if (fileKey && store.progressData[fileKey]) {
        const currentFileProgress = store.progressData[fileKey]
        if (currentFileProgress.uploadId && currentFileProgress.objectToken) {
            cancelSliceUploadFetch({
                appId: currentFileProgress.appInfo && currentFileProgress.appInfo.appId,
                uploadId: currentFileProgress.uploadId,
                objectToken: currentFileProgress.objectToken,
            }).catch(() => {})
        }
        store.progressData = update(store.progressData, {
            [fileKey]: {
                status: {
                    $set: UPLOAD_STATUS.CANCEL,
                },
            },
        })
        PubSub.publish(PubSubTopic, store.progressData)
        return store.progressData[fileKey]
    }
}

export const deleteTask = fileKey => {
    //  取消或者删除状态的上传任务支持从列表中删除
    if (
        fileKey &&
        store.progressData[fileKey] &&
        [UPLOAD_STATUS.FAIL, UPLOAD_STATUS.CANCEL].includes(store.progressData[fileKey].status)
    ) {
        store.progressData = update(store.progressData, {
            $unset: [fileKey],
        })
        PubSub.publish(PubSubTopic, store.progressData)
    }
}

export const subscribeProgress = callbackFn => {
    return PubSub.subscribe(PubSubTopic, (msg, data) => {
        if (msg === PubSubTopic) {
            callbackFn(data)
        }
    })
}
export const unsubscribeProgress = PubSub.unsubscribe
